import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Circle } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ShieldAlert, Clock, MapPin, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const liveIcon = L.divIcon({
  className: "live-marker",
  html: `<div style="
    width: 24px; height: 24px; background: #dc2626;
    border: 3px solid white; border-radius: 50%;
    box-shadow: 0 0 0 6px rgba(220,38,38,.25), 0 2px 8px rgba(0,0,0,.3);
    animation: pulse 1.4s ease-out infinite;
  "></div>
  <style>@keyframes pulse{0%{box-shadow:0 0 0 0 rgba(220,38,38,.5)}70%{box-shadow:0 0 0 16px rgba(220,38,38,0)}100%{box-shadow:0 0 0 0 rgba(220,38,38,0)}}</style>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

export default function SharePage() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchShare = async () => {
    try {
      const res = await axios.get(`${API}/sos/share/${shareId}`);
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.status === 404 ? "Share link not found" : "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShare();
    const t = setInterval(fetchShare, 15000); // refresh every 15s
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [shareId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="share-error">
        {error}
      </div>
    );
  }

  const lastUpdate = data?.last_update_at ? new Date(data.last_update_at).toLocaleString() : "—";
  const statusColor = data.is_active ? "bg-red-600" : "bg-slate-400";
  const center = [data.latitude, data.longitude];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="share-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
          <ShieldAlert className="text-red-600" size={22} />
          <div className="flex-1">
            <h1 className="font-bold">Live SOS — {data.user_name}</h1>
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <Clock size={12} />
              Updated {lastUpdate}
            </p>
          </div>
          <span className={`text-white text-xs font-bold px-2 py-1 rounded-full ${statusColor}`}>
            {data.is_active ? "ACTIVE" : "ENDED"}
          </span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 space-y-4">
        <div className="rounded-2xl overflow-hidden shadow-sm border border-slate-200" style={{ height: "60vh" }}>
          <MapContainer center={center} zoom={16} style={{ height: "100%", width: "100%" }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={center} icon={liveIcon} />
            {data.accuracy && <Circle center={center} radius={data.accuracy} pathOptions={{ color: "#dc2626", fillOpacity: 0.05 }} />}
          </MapContainer>
        </div>

        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-200 space-y-2">
          <div className="flex items-start gap-2">
            <MapPin size={18} className="text-red-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-slate-500">Coordinates</p>
              <p className="font-mono text-sm">{data.latitude?.toFixed(6)}, {data.longitude?.toFixed(6)}</p>
            </div>
          </div>
          <a
            href={data.google_maps_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-sky-600 hover:underline text-sm"
            data-testid="share-open-maps"
          >
            Open directions in Google Maps <ExternalLink size={14} />
          </a>
          {data.note && (
            <p className="text-sm text-slate-700 bg-slate-50 p-2 rounded">Note: {data.note}</p>
          )}
        </div>

        <p className="text-xs text-slate-400 text-center">
          This page auto-refreshes every 15 seconds while the SOS is active. Expires {data.expires_at ? new Date(data.expires_at).toLocaleString() : "soon"}.
        </p>
      </main>
    </div>
  );
}
